import { Contract, PlanfactOperation, Task, WorkType } from '../services/api';
import { ACTIVE_CONTRACT_STATUS } from '../components/ContractPicker';
import { resolveContractFamily, sumFamilyAmount, maxFamilyDate } from './contractFamily';
import { resolveFamilyFinance, sumReceiptsByProjectName } from './contractReceipts';
import { formatDeadlineRelativeTo, ContractDeadlineInfo } from './deadline';
import { resolveFamilyExpenseReferenceDate } from './contractDeadlines';
import { expenseAmountMagnitude } from './operationBudgetArticle';
import { isTaskArchived, isTaskInWork } from './taskStatus';

export interface WorkTypeCount {
  workTypeId: number;
  label: string;
  count: number;
}

export type TaskMatrixColumn = 'project' | 'montage' | 'startup';
export type TaskMatrixRow = 'fl' | 'ul';

export interface TaskMatrixCell {
  overdue: number;
  total: number;
}

export interface TasksMatrix {
  columns: TaskMatrixColumn[];
  rows: TaskMatrixRow[];
  counts: Record<TaskMatrixRow, Record<TaskMatrixColumn, number>>;
}

export interface TasksOverdueMatrix {
  columns: TaskMatrixColumn[];
  rows: TaskMatrixRow[];
  cells: Record<TaskMatrixRow, Record<TaskMatrixColumn, TaskMatrixCell>>;
}

export interface BriefingContractItem {
  id: number;
  contractNumber: string;
  address?: string;
  contractorName?: string;
  overdue: boolean;
  deadline: ContractDeadlineInfo;
}

export interface BriefingTaskItem {
  id: number;
  contractsTaskId: number;
  taskId: string;
  shortName: string;
  contractNumber?: string;
  overdue: boolean;
  deadline: ContractDeadlineInfo;
  column: TaskMatrixColumn | null;
  row: TaskMatrixRow | null;
}

export interface PortfolioBriefingData {
  /** Число семей договоров (главный + ДС). */
  contractCount: number;
  portfolioSum: number;
  paymentPaid: number;
  paymentPercent: number;

  taskCount: number;
  tasksInWorkMatrix: TasksMatrix;
  overdueTasksMatrix: TasksOverdueMatrix;
  /** Семьи в портфеле — для drill-down на дашборде v2. */
  contractItems: BriefingContractItem[];
  /** Задачи в работе — для drill-down на дашборде v2. */
  taskItems: BriefingTaskItem[];

  overdueContractCount: number;
  overdueContractPercent: number;

  overdueTaskCount: number;
  overdueTaskPercent: number;

  cashFlow: number;
  receiptsTotal: number;
  expenseFact: number;
  expensePlan: number | null;
  budgetRemainder: number | null;
  budgetPercent: number | null;
  budgetOverrun: boolean;

  /** Остаток оплат по активным задачам с закрытием расходов, без отметки «Перерасчет». */
  conceptualDebt: number;
  conceptualDebtPercent: number | null;
  /** Сумма по договору всех закрытых задач (каждая связь contracts_tasks). */
  closedTasksAmount: number;

  /** Заглушка до ведения актов. */
  receivables: null;
}

const EMPTY_MATRIX_COUNTS = (): TasksMatrix['counts'] => ({
  fl: { project: 0, montage: 0, startup: 0 },
  ul: { project: 0, montage: 0, startup: 0 },
});

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function percentOf(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return clampPercent((part / whole) * 100);
}

/** Плановые расходы из tasks_cost: себестоимость + налог + сервисный сбор + комиссия. */
function sumTaskCostPlan(tasks: Task[]): number | null {
  let hasValue = false;
  let sum = 0;
  for (const task of tasks) {
    const parts = [task.planned_cost, task.taxes, task.service_fee, task.partner_commission];
    for (const raw of parts) {
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        sum += n;
        hasValue = true;
      }
    }
  }
  return hasValue ? sum : null;
}

function uniqueTasksByBusinessId(tasks: Task[]): Task[] {
  const byId = new Map<string, Task>();
  for (const task of tasks) {
    const key = task.task_id.trim();
    if (!key || byId.has(key)) continue;
    byId.set(key, task);
  }
  return [...byId.values()];
}

function taskDeadlineInfo(task: Task, referenceDate: string): ContractDeadlineInfo {
  const closing = task.expense_closing_date?.trim();
  const end = task.end_date?.trim();
  if (closing) {
    if (!end) {
      return { label: null, months: null, days: null, overdue: false };
    }
    return formatDeadlineRelativeTo(end, closing);
  }
  return formatDeadlineRelativeTo(end, referenceDate);
}

function isTaskOverdue(task: Task, referenceDate: string): boolean {
  return taskDeadlineInfo(task, referenceDate).overdue;
}

function buildWorkTypeMaps(workTypes: WorkType[]): {
  labelOf: (id: number) => string;
  nameOf: (id: number) => string;
} {
  const byId = new Map(workTypes.map((wt) => [wt.id, wt]));
  return {
    labelOf: (id: number) => {
      const wt = byId.get(id);
      if (!wt) return `Тип ${id}`;
      return wt.short_name?.trim() || wt.name;
    },
    nameOf: (id: number) => byId.get(id)?.name ?? '',
  };
}

function resolveMatrixColumn(workTypeName: string): TaskMatrixColumn | null {
  const name = workTypeName.trim().toLowerCase();
  if (!name) return null;
  if (name.includes('проект')) return 'project';
  if (name.includes('монтаж')) return 'montage';
  if (name.includes('пуск')) return 'startup';
  return null;
}

function resolveMatrixRow(workTypeName: string): TaskMatrixRow | null {
  const name = workTypeName.trim().toUpperCase();
  if (!name) return null;
  if (name.includes('ФЛ')) return 'fl';
  if (name.includes('ЮЛ')) return 'ul';
  return null;
}

function buildTasksMatrix(
  tasks: Task[],
  workTypeNameOf: (id: number) => string,
): TasksMatrix {
  const counts = EMPTY_MATRIX_COUNTS();
  for (const task of tasks) {
    const workTypeName = workTypeNameOf(task.work_type_id);
    const column = resolveMatrixColumn(workTypeName);
    const row = resolveMatrixRow(workTypeName);
    if (column == null || row == null) continue;
    counts[row][column] += 1;
  }
  return {
    columns: ['project', 'montage', 'startup'],
    rows: ['fl', 'ul'],
    counts,
  };
}

function buildOverdueTasksMatrix(
  tasksInWork: Task[],
  overdueTasks: Task[],
  workTypeNameOf: (id: number) => string,
): TasksOverdueMatrix {
  const totalMatrix = buildTasksMatrix(tasksInWork, workTypeNameOf);
  const overdueMatrix = buildTasksMatrix(overdueTasks, workTypeNameOf);
  const cells = EMPTY_MATRIX_COUNTS() as unknown as TasksOverdueMatrix['cells'];

  for (const row of totalMatrix.rows) {
    for (const column of totalMatrix.columns) {
      cells[row][column] = {
        overdue: overdueMatrix.counts[row][column],
        total: totalMatrix.counts[row][column],
      };
    }
  }

  return {
    columns: totalMatrix.columns,
    rows: totalMatrix.rows,
    cells,
  };
}

/** Сумма amount и остаток оплат по закрытым активным задачам (без отметки «Перерасчет»). */
function sumClosedTasksFinance(
  portfolioTasks: Task[],
  planfactOperations: PlanfactOperation[] = [],
): {
  closedTasksAmount: number;
  conceptualDebt: number;
} {
  let closedTasksAmount = 0;
  const byTaskId = new Map<string, { totalAmount: number; paid: number }>();
  const hasPlanfact = planfactOperations.length > 0;
  const receiptsByTaskId = hasPlanfact ? sumReceiptsByProjectName(planfactOperations) : null;

  for (const task of portfolioTasks) {
    if (isTaskArchived(task.task_status)) continue;
    if (!task.expense_closing_date?.trim()) continue;
    const key = task.task_id.trim();
    if (!key) continue;

    const amount = Number(task.amount) || 0;
    closedTasksAmount += amount;

    let entry = byTaskId.get(key);
    if (!entry) {
      const paid = receiptsByTaskId
        ? receiptsByTaskId.get(key) ?? 0
        : Number(task.payment_paid) || 0;
      entry = { totalAmount: 0, paid };
      byTaskId.set(key, entry);
    }
    entry.totalAmount += amount;
  }

  let conceptualDebt = 0;
  for (const { totalAmount, paid } of byTaskId.values()) {
    const remainder = totalAmount - paid;
    if (remainder > 0.5) {
      conceptualDebt += remainder;
    }
  }

  return { closedTasksAmount, conceptualDebt };
}

function familyHasProjectLead(
  familyContracts: Contract[],
  projectLeadId: number,
): boolean {
  return familyContracts.some((contract) => contract.project_lead_id === projectLeadId);
}

function taskHasProjectLead(
  task: Task,
  contractsById: Map<number, Contract>,
  projectLeadId: number,
  mainContract: Contract,
): boolean {
  const leadId =
    task.project_lead_id
    ?? contractsById.get(task.contract_id)?.project_lead_id
    ?? mainContract.project_lead_id;
  return leadId === projectLeadId;
}

export interface PortfolioBriefingOptions {
  /** Если задан, в сводку входят только договоры и задачи, где этот сотрудник — РП. */
  projectLeadId?: number;
  contractorNameById?: Map<number, string>;
}

/**
 * Сводка портфеля для диалога руководителя на /dashboard-oup.
 * Семьи со статусом главного договора «в работе».
 */
export function buildPortfolioBriefing(
  contracts: Contract[],
  tasks: Task[],
  planfactOperations: PlanfactOperation[] = [],
  workTypes: WorkType[] = [],
  options: PortfolioBriefingOptions = {},
): PortfolioBriefingData {
  const { nameOf } = buildWorkTypeMaps(workTypes);
  const contractsById = new Map(contracts.map((contract) => [contract.id, contract]));
  const projectLeadId = options.projectLeadId;
  const contractorNameById = options.contractorNameById;
  const seenMainIds = new Set<number>();
  const portfolioTasks: Task[] = [];
  const contractItems: BriefingContractItem[] = [];

  let contractCount = 0;
  let overdueContractCount = 0;
  let portfolioSum = 0;
  let paymentPaid = 0;
  let receipts = 0;
  let expensesSigned = 0;

  for (const contract of contracts) {
    const family = resolveContractFamily(contract, contracts);
    const mainId = family.mainContract.id;
    if (seenMainIds.has(mainId)) continue;
    seenMainIds.add(mainId);

    if (family.mainContract.contract_status !== ACTIVE_CONTRACT_STATUS) {
      continue;
    }

    if (projectLeadId != null && !familyHasProjectLead(family.familyContracts, projectLeadId)) {
      continue;
    }

    const familyTasks = tasks.filter((task) => {
      if (!family.familyIds.has(task.contract_id)) {
        return false;
      }
      if (projectLeadId == null) {
        return true;
      }
      return taskHasProjectLead(task, contractsById, projectLeadId, family.mainContract);
    });

    contractCount += 1;
    portfolioTasks.push(...familyTasks);

    const amount = sumFamilyAmount(family.familyContracts);
    portfolioSum += amount;

    const finance = resolveFamilyFinance({
      familyContractIds: family.familyIds,
      familyTasks,
      mainContract: family.mainContract,
      planfactOperations,
    });
    paymentPaid += finance.paymentPaid;
    if (!(projectLeadId != null && familyTasks.length === 0)) {
      receipts += finance.receipts;
      expensesSigned += finance.expenses;
    }

    const maxEndDate = maxFamilyDate(family.familyContracts, 'end_date');
    const expenseReferenceDate = resolveFamilyExpenseReferenceDate(familyTasks);
    const deadline = formatDeadlineRelativeTo(maxEndDate, expenseReferenceDate);
    if (deadline.overdue) {
      overdueContractCount += 1;
    }

    contractItems.push({
      id: family.mainContract.id,
      contractNumber: family.mainContract.contract_number,
      address: family.mainContract.address,
      contractorName: contractorNameById?.get(family.mainContract.contractor_id),
      overdue: deadline.overdue,
      deadline,
    });
  }

  const uniqueTasks = uniqueTasksByBusinessId(portfolioTasks);
  const tasksInWork = uniqueTasks.filter((task) => isTaskInWork(task.task_status));
  const taskCount = tasksInWork.length;
  const tasksInWorkMatrix = buildTasksMatrix(tasksInWork, nameOf);

  const todayIso = new Date().toISOString().slice(0, 10);
  const overdueTasks = tasksInWork.filter((task) => isTaskOverdue(task, todayIso));
  const overdueTaskCount = overdueTasks.length;
  const overdueTasksMatrix = buildOverdueTasksMatrix(tasksInWork, overdueTasks, nameOf);

  const taskItems: BriefingTaskItem[] = tasksInWork.map((task) => {
    const workTypeName = nameOf(task.work_type_id);
    const deadline = taskDeadlineInfo(task, todayIso);
    return {
      id: task.id,
      contractsTaskId: task.contracts_task_id,
      taskId: task.task_id,
      shortName: task.short_name,
      contractNumber: task.contract_number,
      overdue: deadline.overdue,
      deadline,
      column: resolveMatrixColumn(workTypeName),
      row: resolveMatrixRow(workTypeName),
    };
  });

  const expenseFact = expenseAmountMagnitude(expensesSigned);
  const expensePlan = sumTaskCostPlan(uniqueTasks);
  const budgetRemainder = expensePlan != null ? expensePlan - expenseFact : null;
  const budgetPercent =
    expensePlan != null && expensePlan > 0 ? Math.round((expenseFact / expensePlan) * 100) : null;
  const budgetOverrun = budgetRemainder != null && budgetRemainder < -0.5;

  const cashFlow = receipts + expensesSigned;

  const { closedTasksAmount, conceptualDebt } = sumClosedTasksFinance(
    portfolioTasks,
    planfactOperations,
  );
  const conceptualDebtPercent =
    closedTasksAmount > 0 ? clampPercent((conceptualDebt / closedTasksAmount) * 100) : null;

  return {
    contractCount,
    portfolioSum,
    paymentPaid,
    paymentPercent: percentOf(paymentPaid, portfolioSum),

    taskCount,
    tasksInWorkMatrix,
    overdueTasksMatrix,
    contractItems,
    taskItems,

    overdueContractCount,
    overdueContractPercent: percentOf(overdueContractCount, contractCount),

    overdueTaskCount,
    overdueTaskPercent: percentOf(overdueTaskCount, taskCount),

    cashFlow,
    receiptsTotal: receipts,
    expenseFact,
    expensePlan,
    budgetRemainder,
    budgetPercent,
    budgetOverrun,

    conceptualDebt,
    conceptualDebtPercent,
    closedTasksAmount,

    receivables: null,
  };
}
